import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Where "Return to Home" sends users. This page is only reached as a fallback when
// the server didn't 302 the user straight back to their origin, and the traffic is
// overwhelmingly zhiborenqi, so send them back to the add-funds page there.
const HOME_URL = 'https://zhiborenqi.com/addfunds';

const PaymentSuccess = () => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');

    // If this payment originated from zhiborenqi.com, bounce back there.
    if (ref) {
      try {
        const source = atob(ref);
        if (source === 'zhiborenqi.com') {
          window.location.href = 'https://zhiborenqi.com/addfunds?status=success&message=Payment%20successful';
          return;
        }
      } catch (e) {
        // Malformed ref — fall through and show the generic success page.
      }
    }

    // Look up the transaction only if the invoice id is present in the URL.
    // The status endpoint returns a minimal { status, paid, amount } shape (MYR).
    const invoiceId = params.get('invoice_id') || params.get('external_id');
    if (!invoiceId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(`/api/payment-status/${encodeURIComponent(invoiceId)}`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }
        const data = await response.json();
        if (!cancelled && data && data.status !== 'not_found') {
          setOrderDetails({ invoiceId, amount: data.amount, status: data.status });
        }
      } catch (error) {
        // Non-fatal: still show the success confirmation without the detail rows.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOrderDetails();
    return () => { cancelled = true; };
  }, [location]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  return (
    <div className="payment-success">
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h1>Payment Successful!</h1>
        <p>Thank you for your purchase.</p>

        {orderDetails && (
          <div className="order-details">
            <h2>Order Details</h2>
            <div className="detail-item">
              <span>Invoice ID:</span>
              <span>{orderDetails.invoiceId}</span>
            </div>
            {orderDetails.amount != null && (
              <div className="detail-item">
                <span>Amount:</span>
                <span>MYR {Number(orderDetails.amount).toLocaleString()}</span>
              </div>
            )}
            <div className="detail-item">
              <span>Status:</span>
              <span className="status-completed">Completed</span>
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button onClick={() => { window.location.href = HOME_URL; }} className="home-button">
            Return to Home
          </button>
        </div>
      </div>

      <style>{`
        .payment-success {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          padding: 20px;
        }
        .success-container {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
          max-width: 600px;
          width: 100%;
        }
        .success-icon {
          width: 80px;
          height: 80px;
          background: #28a745;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto 20px;
        }
        h1 {
          color: #28a745;
          margin-bottom: 10px;
        }
        .order-details {
          margin: 30px 0;
          text-align: left;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 5px;
        }
        .detail-item {
          display: flex;
          justify-content: space-between;
          margin: 10px 0;
          padding: 10px 0;
          border-bottom: 1px solid #dee2e6;
        }
        .status-completed {
          color: #28a745;
          font-weight: bold;
        }
        .action-buttons {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-top: 30px;
        }
        .home-button {
          padding: 12px 24px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 16px;
          transition: background 0.3s;
          background: #6c757d;
          color: white;
        }
        .home-button:hover {
          background: #5a6268;
        }
        .loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
